/**
 * RTWO Event Bus — Federation Registry
 *
 * Registers per-engine federation channels without fabricating event traffic.
 */

import type { IEngineSourceAdapter } from '../adapters/engine-source-adapter-interface';
import { FederationCompositeEngineSourceAdapter } from '../adapters/federation-composite-engine-source-adapter';
import type { OperationalFederatedEngineSourceAdapter } from '../adapters/operational-federated-engine-source-adapter';

export class EventBusFederationRegistry {
  private readonly channels = new Map<string, OperationalFederatedEngineSourceAdapter>();
  private readonly composite = new FederationCompositeEngineSourceAdapter(() =>
    Array.from(this.channels.values())
  );

  registerChannel(adapter: OperationalFederatedEngineSourceAdapter): void {
    this.channels.set(adapter.sourceModuleId, adapter);
  }

  getChannel(moduleId: string): OperationalFederatedEngineSourceAdapter | null {
    return this.channels.get(moduleId) ?? null;
  }

  getAllChannels(): OperationalFederatedEngineSourceAdapter[] {
    return Array.from(this.channels.values());
  }

  getChannelCount(): number {
    return this.channels.size;
  }

  isFederationActive(): boolean {
    return this.channels.size > 0 && this.channels.values().some((channel) => channel.isReady());
  }

  getCompositeAdapter(): IEngineSourceAdapter {
    return this.composite;
  }
}

let _eventBusFederationRegistry: EventBusFederationRegistry | null = null;

export function getEventBusFederationRegistry(): EventBusFederationRegistry {
  if (!_eventBusFederationRegistry) {
    _eventBusFederationRegistry = new EventBusFederationRegistry();
  }
  return _eventBusFederationRegistry;
}

export const eventBusFederationRegistry = getEventBusFederationRegistry();
